import React, { useState, useMemo, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import AutoSizer from "react-virtualized-auto-sizer";
import { useReactTable,	getCoreRowModel, ColumnDef, flexRender, TableOptions, CellContext, 
	ColumnOrderState, Row, ColumnFiltersState, Header, HeaderGroup, 
} from '@tanstack/react-table';
import { Table as BTable } from 'reactstrap';
import _ from 'lodash';
import classNames from 'classnames';

import RowToolbarCell from './Components/RowToolbar';
import TableCell from './Components/TableCell/TableCell';
import { TableHeaderGroup } from './Components';
import EmptyCell from './Components/EmptyCell/EmptyCell';
import { ExpandColumnCell } from './Components/ExpandColumn/ExpandColumnCell';
import Filter from './Components/TableFilter/TableFilter';

// Hooks
import { useContextMenuHelper } from 'src/Hooks';

// Types
import { ColumnType, DataRowEventType, ITableRowToolbar, TableColumnSizing, TableFilter, TableSort, onDataRowClickHandler } from '.';
import { FileType } from 'src/Types/File';
import { ContextMenuWindow, ContextMenuItemType } from 'src/Components';
import { Button, EmptyPanel } from 'src/UI';

import { MOUSE_CLICK_TIMESTAMP } from 'src/Constants';

import { ReactComponent as ClearFilterIcon } from './Components/assets/clearFilter.svg';
import './NewGrid.scss';

export interface NvxDataGridProps {
	name: string;
	// height?: string | number;
	marginTop?: number; 
	width?: string | number;
	className?: string;
	styles?: React.CSSProperties;

	isWait?: boolean;

	dataKey?: string;
	data: Array<any>;
	columns: Array<ColumnType>;

	allowFilters?: boolean;

	noDataText?: React.ReactNode;
	minColumnSize?: number;
	rowExpanding?: boolean;
	
	/** Флаг раскрытия всех строк */
	isAllRowsExpanded?: boolean;

	isMultipleSelect?: boolean;
	checkBoxesMode?: boolean;
	selectedRecords?: Array<string>;

	sort?: TableSort;
	filter?: TableFilter;
	columnSizing?: TableColumnSizing;
	rowToolbar?: ITableRowToolbar;
	
	onPrepareContextMenu?: ( data: any, selectedRecords: Array<string> ) => ContextMenuItemType[];
	
	/**
	 * Вызывается при клике по строке с данными
	 * @param event Исходный ивент
	 * @param type Тип клика ( ЛКМ, ПКМ, СКМ, Двойной клик )
	 * @param params Данные строки
	 * @returns Если строку после клика выделить не нужно то возвращаем False, иначе строка будет выделена
	 */
	onClick?: onDataRowClickHandler;

	/**
	 * Функция вычисление ссылки
	 * @param data данные строки
	 * @returns Адрес ссылки
	 */
	onCalcLink?: (data: any) => string;

	setSelectedRecords?: Dispatch<SetStateAction<string[]>>;
	bottomToolbarRender?: () => React.ReactNode;
	onSelectionChanged?: ( state: Array<string> ) => void; 
	columnsPrepared?: ( columns: Array<ColumnType> ) => Array<ColumnType>;
	onGetRowClasses?: (rowData: any, isExpanded: boolean) => string;
	clearFilters?: () => void;
};

const NvxDataGrid: React.FC<NvxDataGridProps> = (props) => {

	const { 
		marginTop = 80, data = [], columns, noDataText, dataKey = 'id', sort, filter, columnSizing, styles = {}, 
		minColumnSize = 150, isWait = false,
		isMultipleSelect = false, isAllRowsExpanded = false, allowFilters = false, checkBoxesMode = false, rowToolbar, selectedRecords = [], 
		rowExpanding = false, className = "",
		bottomToolbarRender, onSelectionChanged, setSelectedRecords, clearFilters, onGetRowClasses, onPrepareContextMenu, onClick, onCalcLink
	} = props;

	const wrapperRef = useRef(null);
	const timer = useRef(null);
	// тут будем хранить временную метку mouseDown'а
	const selectionTimeStampRef = useRef<number>(0);
	const [ preparedColumns, setPreparedColumns ] = useState<ColumnDef<FileType>[]>([]);
	const [ columnOrder, setColumnOrder ] = useState<ColumnOrderState>([]);
	const [ isActiveRowToolbarOn, setIsActiveRowToolbarOn ] = useState<string>('');
	const [ scroll, setScroll ] = useState<number>(0);
	const [ contextMenuInfo, onContextMenuHandler, hideContextMenu ] = useContextMenuHelper( !!onPrepareContextMenu );
	const [ rowToolbarMenuInfo, onRowToolbarClickHandler, hideTBMenu ] = useContextMenuHelper( !!rowToolbar && !!rowToolbar.onPrepareMenu );

	const onScrolled = ( e: any ) => {
		if ( !e.target ) return;
		hideContextMenu();
		hideTBMenu();
		if ( !e.target.scrollLeft ) return;
		if ( !!timer.current ) {
			clearTimeout( timer.current );
		} 
		else {
			setTimeout(() => {
				setScroll( e.target.scrollLeft );
			}, 200 );
		}	
	};

	type ExpandStateType = { [Tkey: string]: boolean };
	const [expandState, setExpandState] = React.useState<ExpandStateType>({})
	const toggleExpanded = ( recid: string ) => setExpandState( prev => ({
		...prev,
		[recid]: !prev[recid]
	}));
	const getIsExpanded = (recid: string) => expandState[recid];

	const getColumnInitialWidth = ( column: ColumnType ): number => {
		if ( !columnSizing || !columnSizing.state || !columnSizing.state[column.id] ) {
			const allWidth = !!wrapperRef.current ? (wrapperRef.current as any).state.scaledWidth : table.getTotalSize();
			return !!allWidth ? (allWidth / table.getVisibleFlatColumns().length) - 35 : 0;
		}
		if ( !!columnSizing && !!columnSizing.state[column.id] )
			return columnSizing.state[column.id];
		return !!column.width ? +column.width : minColumnSize;
	};

	/** Следим за данными и флагом и перестраиваем список свернутых/развернутых строк */
	useEffect(() => {
		const state: ExpandStateType = {};
		data.forEach( d => state[d.recid] = isAllRowsExpanded );
		setExpandState(state);
	},[data,isAllRowsExpanded]);

	useEffect( () => {
		hideContextMenu();
		hideTBMenu();
	// eslint-disable-next-line
	}, [ data ] );

	// Приводит изначальный формат описания колонок к формату ReactTable
	useEffect(() => {
		const cols = columns.filter( c => c.isVisible !== false ).map( ( column: ColumnType ) => ({
			...column,
			accessorKey: column.id,
			enableSorting: column.sortable,
			type: column.type || 'string',
			size: !!column.width ? +column.width : getColumnInitialWidth(column),
			minSize: minColumnSize,
 			maxSize: Number.MAX_SAFE_INTEGER,
			enableColumnFilter: column.filterable,
			cell: cellRender,
			footer: (props: any) => props.column.id,
		}));
		setPreparedColumns(cols);
	// eslint-disable-next-line
	}, [columns]);

	// Генерируем объект описывающий видимость колонок
	useEffect(() => {
		const cols = _.sortBy(columns.filter( c => c.isVisible !== false ), [(o) => o.position]).map( (c: ColumnType) => c.id );
		setColumnOrder(cols);
	}, [columns]);

	/** Отрисовываем ячейку таблицы с данными  */
	const cellRender = (props: CellContext<FileType, any>) => {
		const column = columns.find( c => c.id === props.column.id );
		return (
			<TableCell 
				isExpand={getIsExpanded(props.row.original.id)}
				link={!!onCalcLink ? onCalcLink(props.row.original):""}
				column={column} 
				data={props.row.original}
				value={props.getValue()}
				width={ props.column.getSize()  }
			/>
		);
	};

	/** Обработчик выбора строки таблицы */
	const onRowSelect = ( data: any ) => {
		if ( !!setSelectedRecords ) {
			setTimeout( () => {
				setSelectedRecords( prev => {
					let nextStateSelect = [...prev];
					const index = nextStateSelect.findIndex( i => i === data[dataKey] );
					if ( index !== -1 ) {
						nextStateSelect.splice( index, 1 );
					}
					else if ( isMultipleSelect ) {
						nextStateSelect.push(data[dataKey]);
					}
					else {
						nextStateSelect = [ data[dataKey] ];
					}

					if ( !!onSelectionChanged ) {
						onSelectionChanged(nextStateSelect);
					}
					return nextStateSelect;
				});
			}, 50 );
		}
	};

	/** Функция определяет последняя ли колонка */
	const isLastColumn = ( id: string ): boolean => columnOrder.findIndex( c => c === id ) === (columnOrder.length - 1);

	/** Возвращает ширину колонки  */
	const getColumnWidth = ( header: Header<FileType, unknown> ): number => {
		const isLast = isLastColumn( header.id );
		if ( isLast ) {
			return 0;
		}
		return header.getSize();
	};

	/////////////////////// EVENTS ///////////////////////////////////////////////////////////////////////

	/** Обработчик события MouseDown */
	const onMouseDown = ( event: React.MouseEvent<Element, MouseEvent> ) => {
		// Если нажата ЛКМ
		if ( event.button === 0 ) {
			// запомним временную ветку
			selectionTimeStampRef.current = event.timeStamp;
			return;
		}
	};

	/** Обработчик события MouseUp */
	const onMouseUp = ( event: React.MouseEvent<Element, MouseEvent>, params: any ) => {
		// Если нажата ЛКМ
		if ( event.button === 0 ) {
			// получаем разницу между нажатием и отпуском ЛКМ
			const diffTimeStamp = event.timeStamp - selectionTimeStampRef.current;
			// Если дольше константы
			if ( diffTimeStamp > MOUSE_CLICK_TIMESTAMP ) {
				// получаем выделенный текст
				const selected_text = window.getSelection()?.toString();
				// Если он есть, то выходим из функции, пущай юзер копирует...
				if(!!selected_text) {
					// тут в дальнейшим можно будет замутить автокопирование в буфер...
					return;
				}
			}
			// Костыльно конечно, но самый быстрый и быстродействующий способ
			// игнорить клик по кнопкам и инпутам
			const targetTagName = (event.target as Element).tagName.toLowerCase();
			const isInsideButton = !!(event.target as Element).closest('button');
			if ( [ "input", "svg", "path", "button" ].includes(targetTagName) || isInsideButton ) {
				return;
			}
			
		}
		// далее обрабатываем клик обычно
		if ( !!onClick ) {
			let type: DataRowEventType = "click";
			if ( event.button == 1 ) {
				type = "whClick";
			}
			else if ( event.button === 2 ) {
				type = "rightClick";
			}
			const result = onClick( event as React.MouseEvent<HTMLTableRowElement, MouseEvent>, type, params );
			if ( result !== undefined && result !== false ) {
				onRowSelect( params );
			}
		}
	};

	const onPrepareContextMenuHandler = ( data: unknown ): ContextMenuItemType[] => {
		if ( !!onPrepareContextMenu ) {
			return onPrepareContextMenu( data, selectedRecords );
		}
		return [];
	};

	const onPrepareRowTbMenuHandler = ( data: unknown ): ContextMenuItemType[] => {
		if ( !!rowToolbar?.onPrepareMenu ) {
			return rowToolbar?.onPrepareMenu( data, selectedRecords );
		}
		return [];
	};

	/////////////////////// EVENTS ///////////////////////////////////////////////////////////////////////

	/** Проверка на наличие изменений в фильтрах */
	const checkChanged = () => {
		if ( !filter && !sort && !columnSizing ) return false;
		return filter?.state.filter( f => !!f.value ).length;
	};

	const onClearFilters = () => {
		if ( clearFilters )
			clearFilters();
	};

	const renderClearFilter = () => {
		if ( !clearFilters && ( !rowExpanding && !checkBoxesMode ) ) 
			return null; 
		else if ( !clearFilters || !checkChanged() ) 
			return ( <EmptyCell isHeader width={32} /> );

		return (
			<th className='clear_filter' style={{ width: '32px' }} >
				<Button 
					id="btn_clearfilters" 
					data-tooltip-id="rdc_tooltip" 
					data-tooltip-content="Сбросить все фильтры" 
					outline 
					onClick={onClearFilters}
				>
					<ClearFilterIcon />
				</Button>
			</th>
		);
	};

	/** Отрисовка фильтра */
	const renderFilter = ( columnId: string ) => (
		<Filter
			columnId={columnId}
			columnTypes={columns}
			filter={filter}
		/>
	);

	/**  Рендер шапки таблицы */
	const renderTableHeader = ( group: HeaderGroup<FileType>, index: number ) => (
		<TableHeaderGroup
			key={"headerGroup_" + index}
			group={group}
			sort={sort}
			minColumnSize={minColumnSize}
			allowFilters={allowFilters}
			renderFirstToolbar={renderClearFilter}
			renderFilter={renderFilter}
		/>
	);

	/**  Рендер ячейки таблицы */
	const renderCell = (cell: any) => (
		<td key={cell.id} width={getColumnWidth(cell.column)} is-data-cell="true">
			{flexRender(cell.column.columnDef.cell,	cell.getContext())}
		</td>
	);

	const renderRowToolbar = ( data: any ) => {
		if ( !!rowToolbar && !!rowToolbar.onPrepareMenu ) 
			return (
				<RowToolbarCell 
					active = { isActiveRowToolbarOn === data[dataKey] }
					data = { data }
					options = { rowToolbar }
					onClick={ (e) => {
						setIsActiveRowToolbarOn(data[dataKey]);
						onRowToolbarClickHandler( e, data );	
					}}
				/>
			)
	};

	/**  Рендер строки таблицы */
	const renderRow = (row: any) => {
		const recid = row.original[dataKey]; 
		const isExpand = getIsExpanded(recid);
		const externClass = !!onGetRowClasses ? onGetRowClasses(row, !!isExpand) : '';
		const rowClasses = classNames('rdc_datarow', externClass, { 
			'row_selected': !!selectedRecords && selectedRecords.includes(recid),
			'row_expanded': isExpand
		});
		return (
			<tr 
				key={row.id}
				id={recid}
				onMouseDown={onMouseDown}
				onMouseUp={e => onMouseUp(e, row.original)}
				onContextMenu={ e => onContextMenuHandler( e, row.original ) }
				className={rowClasses}
			>
				{ 
					( rowExpanding || checkBoxesMode ) && 
					<ExpandColumnCell 
						rowId={recid}
						isExpand={isExpand}
						rowExpanding={rowExpanding}
						onExpand={toggleExpanded}
						checkBoxesMode={checkBoxesMode}
						selectedRecords={selectedRecords}
						onRowSelect={ () => onRowSelect(row.original) }
					/> 
				}			
				{ row.getVisibleCells().map( (cell: any) => renderCell(cell)) }
				{ renderRowToolbar( row.original ) }
			</tr>
		);
	};

	/**  Рендер строк таблицы */
	const renderTableRows = (rows: Row<FileType>[]) => {
		if ( !!rows.length )
			return rows.map(row => renderRow(row));
		else 
			return null;
	}

	const renderLoading = () => (
		<div className="rdc_table__emptypanel">
			<div className="rdct_table-loader rdc_spiner" />
			<p className="emptypanel_text">Ищем дела...</p>
		</div>
	);

	const renderNoData = () => (
		<EmptyPanel className="rdc_table__emptypanel" isRow hideIcon={!!noDataText && typeof noDataText !== "string" && typeof noDataText !== "number"}>
			{noDataText}
		</EmptyPanel>
	);

	const renderMessages = ( rowsCount?: number ) => {
		if ( isWait )
			return renderLoading();
		else if ( !rowsCount )
			return renderNoData();
		else 
			return null;
	};

	// Опции таблицы
	const gridOptions: TableOptions<FileType> = useMemo( () => {
		const option: TableOptions<FileType> = {
			data,
			state: {
				columnOrder,
				columnSizing: !!columnSizing && columnSizing.state ? columnSizing.state : undefined
			},
			getCoreRowModel: getCoreRowModel(),
			columns: preparedColumns,
			enableColumnResizing: true,
			columnResizeMode: 'onChange'
		};

		if ( !!columnSizing && !!columnSizing?.setColumnSize ) {
			option.onColumnSizingChange = columnSizing.setColumnSize;
		}

		return option;
	// eslint-disable-next-line
	}, [preparedColumns, data, columnSizing?.state] );

	const table = useReactTable(gridOptions);
	const rows = table.getRowModel().rows;

	if ( !preparedColumns.length )
		return null;

	const getTotalSize = (): number => {
		const w = table.getTotalSize();
		return ( w - 64 );
	};

	const renderTable = (props: any) => {
		const { height, width, scaledWidth } = props;
		return (
			<div id="rdct_table" onScrollCapture={onScrolled} className={classNames("rdc_datagrid_wrapper new_datagrid_wrapper body-small", className)} style={{ height: (height-marginTop), width, ...styles }}>
				<BTable responsive style={{ minWidth: ( width - 15 ), width: getTotalSize() }}> 
					{ table.getHeaderGroups().map( renderTableHeader ) }
					<tbody>
						{renderTableRows(rows)}
						<tr className='row_toolbar nothovered'>
							<td className='more_btn' style={{ transform: `translate( calc(-50% + ${(scaledWidth/2)+scroll}px), 0` }}>
								{ ( !!data.length && !!bottomToolbarRender ) && bottomToolbarRender()}
							</td>
						</tr>
					</tbody>
				</BTable>
				{ renderMessages(rows.length) }
			</div>
		);
	};

	return (
		<>
			<AutoSizer ref={wrapperRef}>
				{renderTable}
			</AutoSizer>
			{ 
				!!rowToolbar?.onPrepareMenu && 
				<ContextMenuWindow
					right={false} 
					paddingX={-25} 
					onPrepareContextMenu={onPrepareRowTbMenuHandler} 
					{...rowToolbarMenuInfo} 
					onClose={ () => setIsActiveRowToolbarOn('') }
					onOutSideClick={ (e) => {
						setIsActiveRowToolbarOn('');
						hideTBMenu();
					}} 
				/> 
			}
			{ 
				!!onPrepareContextMenu && 
				<ContextMenuWindow 
					onClose={ () => setIsActiveRowToolbarOn('') } 
					onPrepareContextMenu={onPrepareContextMenuHandler} 
					{...contextMenuInfo} 
				/> 
			}
		</>
	);
};

export type { ColumnFiltersState };

export default NvxDataGrid;
